import { MapPin } from "lucide-react";

/**
 * Stylized abstract SVG of the GTA + Golden Horseshoe region.
 * Not geographically precise — design-led, brand-coloured.
 * North York highlighted as the HQ pin.
 */
const GtaMap = ({ className = "" }) => (
    <div className={`relative w-full ${className}`} data-testid="gta-map">
        <svg
            viewBox="0 0 800 560"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-auto block bg-brand-dark"
            role="img"
            aria-label="Reformity Inc. service area map — GTA and Golden Horseshoe"
        >
            {/* Background land mass */}
            <rect width="800" height="560" fill="#3d3d3d" />

            {/* Grid pattern */}
            <defs>
                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#ffffff" strokeOpacity="0.04" strokeWidth="1" />
                </pattern>
                <linearGradient id="lakeGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3dbfce" stopOpacity="0.18" />
                    <stop offset="100%" stopColor="#3dbfce" stopOpacity="0.05" />
                </linearGradient>
            </defs>
            <rect width="800" height="560" fill="url(#grid)" />

            {/* Service-area highlight zone (a soft cyan blob covering the GTA/GHB) */}
            <path
                d="M 80 100 Q 200 60 380 80 Q 560 70 700 130 Q 760 200 740 320 Q 700 420 540 460 Q 360 490 180 460 Q 80 420 60 320 Q 50 200 80 100 Z"
                fill="#3dbfce"
                fillOpacity="0.07"
                stroke="#3dbfce"
                strokeOpacity="0.35"
                strokeWidth="1.5"
                strokeDasharray="4 6"
            />

            {/* Lake Ontario (stylized) */}
            <path
                d="M 0 460 Q 200 480 400 470 Q 600 460 800 480 L 800 560 L 0 560 Z"
                fill="url(#lakeGrad)"
            />
            <path
                d="M 0 460 Q 200 480 400 470 Q 600 460 800 480"
                fill="none"
                stroke="#3dbfce"
                strokeOpacity="0.5"
                strokeWidth="2"
            />
            <text
                x="400"
                y="520"
                textAnchor="middle"
                fontSize="13"
                letterSpacing="3"
                fill="#3dbfce"
                fillOpacity="0.5"
                fontFamily="Montserrat, system-ui"
                fontWeight="600"
            >
                LAKE ONTARIO
            </text>

            {/* Major highway lines (abstract) */}
            <line x1="60" y1="280" x2="740" y2="300" stroke="#ffffff" strokeOpacity="0.08" strokeWidth="2" />
            <line x1="540" y1="60" x2="560" y2="470" stroke="#ffffff" strokeOpacity="0.08" strokeWidth="2" />

            {/* City dots — coordinates are stylized, not geographic */}
            {/* Peel region */}
            <CityDot x={260} y={250} label="Brampton" />
            <CityDot x={310} y={340} label="Mississauga" />
            <CityDot x={210} y={170} label="Caledon" />
            <CityDot x={220} y={210} label="Bolton" small />

            {/* Toronto cluster */}
            <CityDot x={500} y={360} label="Toronto" big />
            <CityDot x={490} y={310} label="North York" hq />
            <CityDot x={430} y={370} label="Etobicoke" small />
            <CityDot x={580} y={370} label="Scarborough" small />

            {/* York region */}
            <CityDot x={440} y={250} label="Vaughan" />
            <CityDot x={490} y={210} label="Richmond Hill" small />
            <CityDot x={570} y={240} label="Markham" />
            <CityDot x={510} y={140} label="Aurora" small />
            <CityDot x={520} y={100} label="Newmarket" small />

            {/* Halton region */}
            <CityDot x={230} y={400} label="Oakville" />
            <CityDot x={170} y={420} label="Burlington" />
            <CityDot x={150} y={300} label="Milton" />
            <CityDot x={100} y={250} label="Halton Hills" small />

            {/* Hamilton */}
            <CityDot x={90} y={420} label="Hamilton" />
            <CityDot x={130} y={440} label="Stoney Creek" small />
        </svg>
    </div>
);

const CityDot = ({ x, y, label, hq = false, big = false, small = false }) => {
    const r = hq ? 9 : big ? 7 : small ? 4 : 6;
    const color = hq ? "#e8564a" : "#3dbfce";
    return (
        <g>
            {hq && (
                <>
                    <circle cx={x} cy={y} r={20} fill={color} fillOpacity="0.15">
                        <animate
                            attributeName="r"
                            from="14"
                            to="26"
                            dur="2.4s"
                            repeatCount="indefinite"
                        />
                        <animate
                            attributeName="opacity"
                            from="0.4"
                            to="0"
                            dur="2.4s"
                            repeatCount="indefinite"
                        />
                    </circle>
                </>
            )}
            <circle cx={x} cy={y} r={r} fill={color} stroke="#ffffff" strokeWidth={hq ? 2 : 1} />
            <text
                x={x + (hq ? 14 : 10)}
                y={y + 4}
                fontSize={small ? 11 : 13}
                fontWeight={hq ? 700 : 600}
                fill={hq ? "#ffffff" : "#e5e5e5"}
                fontFamily="Montserrat, system-ui"
            >
                {label}
                {hq && (
                    <tspan
                        fontSize="9"
                        fill="#e8564a"
                        fontWeight={700}
                        dx="6"
                        letterSpacing="1.5"
                    >
                        HQ
                    </tspan>
                )}
            </text>
        </g>
    );
};

export default GtaMap;
